import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import {
  fetchDecisionPortfolio,
  syncDecisionPortfolioFromCloud,
  analyzeDecisionPortfolio,
  fetchDisclosureCoverage,
  getDecisionApiError
} from '../../services/decisionApi';

const DecisionPortfolioContext = createContext(null);

export function DecisionPortfolioProvider({ children }) {
  const [holdings, setHoldings] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [results, setResults] = useState([]);
  const [sync, setSync] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [disclosureCoverage, setDisclosureCoverage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [analyzedOnce, setAnalyzedOnce] = useState(false);
  const loadInFlight = useRef(null);
  const analyzeInFlight = useRef(null);

  const applyPayload = useCallback((data) => {
    if (!data) return;
    setHoldings(Array.isArray(data.holdings) ? data.holdings : []);
    setSummaries(Array.isArray(data.holding_summaries) ? data.holding_summaries : []);
    if (Array.isArray(data.results)) {
      setResults(data.results);
      if (data.analyzed || data.results.length) setAnalyzedOnce(true);
    }
    if (data.sync !== undefined) setSync(data.sync);
    if (data.updated_at !== undefined) setUpdatedAt(data.updated_at);
  }, []);

  const ensureLoaded = useCallback(async ({ force = false } = {}) => {
    if (!force && loadedOnce) return;
    if (loadInFlight.current) return loadInFlight.current;

    setLoading(true);
    setError('');
    loadInFlight.current = (async () => {
      try {
        const [pf, coverage] = await Promise.all([
          // 默认轻量：同步持仓，不分析
          fetchDecisionPortfolio(force ? { sync: 1 } : undefined),
          fetchDisclosureCoverage().catch(() => null)
        ]);
        applyPayload(pf);
        setDisclosureCoverage(coverage);
        setLoadedOnce(true);
      } catch (err) {
        setError(getDecisionApiError(err));
        throw err;
      } finally {
        setLoading(false);
        loadInFlight.current = null;
      }
    })();
    return loadInFlight.current;
  }, [applyPayload, loadedOnce]);

  const syncFromCloud = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await syncDecisionPortfolioFromCloud();
      applyPayload(data);
      setLoadedOnce(true);
      return data;
    } catch (err) {
      setError(getDecisionApiError(err, err.message));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [applyPayload]);

  const analyzeAll = useCallback(async () => {
    if (analyzeInFlight.current) return analyzeInFlight.current;
    setAnalyzing(true);
    setError('');
    analyzeInFlight.current = (async () => {
      try {
        if (!loadedOnce) {
          await ensureLoaded({ force: true });
        }
        const data = await analyzeDecisionPortfolio({ concurrency: 4 });
        applyPayload(data);
        setAnalyzedOnce(true);
        return data;
      } catch (err) {
        setError(getDecisionApiError(err, err.message));
        throw err;
      } finally {
        setAnalyzing(false);
        analyzeInFlight.current = null;
      }
    })();
    return analyzeInFlight.current;
  }, [applyPayload, ensureLoaded, loadedOnce]);

  /** 单产品页分析完成后回写列表，避免返回后仍显示「未分析」 */
  const upsertResult = useCallback((row) => {
    if (!row || row.product_id == null) return;
    const pid = String(row.product_id);
    const stamped = {
      ...row,
      analyzed_at: row.analyzed_at || new Date().toISOString()
    };
    setResults((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      const idx = next.findIndex((r) => String(r.product_id) === pid);
      if (idx >= 0) next[idx] = { ...next[idx], ...stamped };
      else next.push(stamped);
      return next;
    });
    setAnalyzedOnce(true);
  }, []);

  const value = useMemo(() => ({
    holdings,
    summaries,
    results,
    sync,
    updatedAt,
    disclosureCoverage,
    loading,
    analyzing,
    error,
    loadedOnce,
    analyzedOnce,
    ensureLoaded,
    syncFromCloud,
    analyzeAll,
    upsertResult
  }), [
    holdings,
    summaries,
    results,
    sync,
    updatedAt,
    disclosureCoverage,
    loading,
    analyzing,
    error,
    loadedOnce,
    analyzedOnce,
    ensureLoaded,
    syncFromCloud,
    analyzeAll,
    upsertResult
  ]);

  return (
    <DecisionPortfolioContext.Provider value={value}>
      {children}
    </DecisionPortfolioContext.Provider>
  );
}

export function useDecisionPortfolio() {
  const ctx = useContext(DecisionPortfolioContext);
  if (!ctx) {
    throw new Error('useDecisionPortfolio 必须在 DecisionPortfolioProvider 内使用');
  }
  return ctx;
}
